import { useCallback, useEffect, useState } from "react";
import ResizeObserver from "resize-observer-polyfill";

// we return an array like [the element, set the element callback, height, width]
type ReturnType<T extends HTMLElement> = [T | null, (el: T) => void, number, number];

type Dimensions = Readonly<{
  height: number;
  width: number;
}>;
const DEFAULT_DIMENSIONS: Dimensions = {
  height: 0,
  width: 0,
};

export const useResizableElement = <T extends HTMLElement>(): ReturnType<T> => {
  const [ref, setRef] = useState<T | null>(null);
  const [dimensions, setDimensions] = useState<Dimensions>(DEFAULT_DIMENSIONS);
  const resizeHandler = useCallback(() => {
    if (!ref) {
      return;
    }
    const height = parseFloat(ref.clientHeight.toFixed(2));
    const width = parseFloat(ref.clientWidth.toFixed(2));
    setDimensions({ height, width });
  }, [ref, setDimensions]);
  useEffect(resizeHandler, [ref, setDimensions]);
  useEffect(() => {
    const ro = new ResizeObserver(resizeHandler);
    if (ref) {
      ro.observe(ref);
    }
    return () => {
      ro.disconnect();
    };
  }, [ref, resizeHandler]);
  return [ref, setRef, dimensions.height, dimensions.width];
};
